import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Login.css';
import API_URL from '../config';

const API = API_URL;

function ForgotPassword() {
  const [step, setStep] = useState(1); // 1=email, 2=code, 3=new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      // Always advance to step 2 to avoid user enumeration
      setStep(2);
    } catch {
      setError('Erreur réseau, veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/verify-reset-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Code invalide ou expiré');
      }
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Erreur lors de la réinitialisation');
      }
      navigate('/login');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <img src="https://risingcard.fr/wp-content/uploads/2024/12/Logo-rising-card.svg" alt="Rising Card" />
          </Link>
          <h1>Mot de passe oublié</h1>
          <p>
            {step === 1 && 'Entrez votre email pour recevoir un code de réinitialisation'}
            {step === 2 && 'Entrez le code à 6 chiffres reçu par email (valide 15 min)'}
            {step === 3 && 'Choisissez votre nouveau mot de passe'}
          </p>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#ef4444" strokeWidth="1.5"/>
              <path d="M12 8V12" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="12" cy="16" r="1" fill="#ef4444"/>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {step === 1 && (
          <form className="auth-form" onSubmit={handleSendCode}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                autoComplete="email"
              />
            </div>
            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? 'Envoi...' : 'Envoyer le code'}
            </button>
            <div className="auth-footer">
              <p>
                <Link to="/login" className="auth-link">Retour à la connexion</Link>
              </p>
            </div>
          </form>
        )}

        {step === 2 && (
          <form className="auth-form" onSubmit={handleVerifyCode}>
            <div className="form-group">
              <label htmlFor="code">Code à 6 chiffres</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                required
                maxLength={6}
                style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.4rem' }}
              />
            </div>
            <button type="submit" className="auth-button" disabled={loading || code.length !== 6}>
              {loading ? 'Vérification...' : 'Vérifier le code'}
            </button>
            <div className="auth-footer">
              <p>
                <button type="button" className="auth-link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={() => setStep(1)}>
                  Renvoyer un code
                </button>
              </p>
            </div>
          </form>
        )}

        {step === 3 && (
          <form className="auth-form" onSubmit={handleResetPassword}>
            <div className="form-group">
              <label htmlFor="newPassword">Nouveau mot de passe</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 caractères"
                required
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Retapez votre mot de passe"
                required
                autoComplete="new-password"
              />
            </div>
            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
